<script setup lang="ts" generic="T">
/* eslint-disable max-lines */
import debounceFn from 'debounce';
import { computed, inject, ref, watch, nextTick, onMounted } from 'vue';
import type { Ref } from 'vue';
import { DtInput } from '@dialpad/dialtone/vue3/lib/input';
import { DtListItemGroup } from '@dialpad/dialtone/vue3/lib/list-item-group';
import { DtListItem } from '@dialpad/dialtone/vue3/lib/list-item';
import { DtIcon } from '@dialpad/dialtone/vue3/lib/icon';
import { DtRecipeComboboxWithPopover } from '@dialpad/dialtone/vue3/lib/combobox-with-popover';
import { DtButton } from '@dialpad/dialtone/vue3/lib/button';
import { TRIGGER_FORM_VALIDATION } from '@/app/utils/keys';
import type { ResponseError } from '@/apis/generated/common/runtime';
import { DATA_QA, SearchDropdownError } from './search-dropdown.constants';
import logger from '@/logger';

export type Item<T> = {
  key: string | number;
  title: string;
  subtitle?: string;
  icon?: string | null;
  iconClasses?: string | string[] | null;
  titleClasses?: string | string[] | null;
  disabled?: boolean;
  comparableValue?: number;
  ref: T;
};

export type ItemGroup<T> = {
  title?: string;
  noDivider?: boolean;
  items: Item<T>[];
};

const log = logger.getChild('SearchDropdown');
log.debug('Mounting');

onMounted(() => {
  log.debug('Mounted');
});

enum SearchDropdownMessage {
  NO_RESULTS = 'NO_RESULTS',
  ERROR_LOADING_RESULTS = 'ERROR_LOADING_RESULTS',
}

type Props<T> = {
  groups: ItemGroup<T>[];
  messages: Record<string, string>;
  debounce?: number;
  label?: string;
  description?: string;
  placeholder?: string;
  listAriaLabel?: string;
  staticGroups?: ItemGroup<T>[] | null;
  disabled?: boolean;
  required?: boolean;
  error?: ResponseError | null;
  loading?: boolean;
  errorMessages?: Record<string, string> | null;
  showListIcons?: boolean;
  listClass?: string | string[] | null;
  rightIcon?: string | null;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | null;
  minValue?: number | null;
  maxValue?: number | null;
  clearable?: boolean;
};

const props = withDefaults(defineProps<Props<T>>(), {
  debounce: 300,
  label: '',
  description: '',
  placeholder: '',
  listAriaLabel: '',
  staticGroups: null,
  disabled: false,
  required: false,
  error: null,
  loading: false,
  errorMessages: null,
  showListIcons: true,
  listClass: null,
  rightIcon: null,
  size: null,
  minValue: null,
  maxValue: null,
  clearable: false,
});

const model = defineModel<Item<T> | null>({
  default: null,
});

const emit = defineEmits<{
  (e: 'update:search', searchTerm: string): void;
  (e: 'update:opened', isOpen: boolean): void;
  (e: 'update:error', error: SearchDropdownError | null): void;
}>();

const combobox = ref<InstanceType<typeof DtRecipeComboboxWithPopover>>(null);

const filter = ref('');
const isOpen = ref(false);
const searchInput = ref<InstanceType<typeof DtInput>>(null);
const validationError = ref<SearchDropdownError | null>(null);
const inDebounce = ref(false);

const loadingOrDebounce = computed(() => props.loading || inDebounce.value);

const groupsWithItems = computed(() => {
  return props.groups.filter((group) => group.items.length > 0);
});

const searchUpdate = async (searchTerm: string) => {
  log.debug('Search update trigger', searchTerm);
  emit('update:search', searchTerm);
  await nextTick();
  inDebounce.value = false;
};

const debouncedSearchUpdate = computed(() => {
  return debounceFn(searchUpdate, props.debounce);
});

const search = async (searchTerm: string) => {
  if (searchTerm.length === 0 || props.debounce === 0) {
    log.debug('Immediate search update', searchTerm);
    debouncedSearchUpdate.value.clear();
    void searchUpdate(searchTerm);
    return;
  }

  inDebounce.value = true;
  log.debug('Debounced search update', searchTerm);
  void debouncedSearchUpdate.value(searchTerm);
};

const searchTerm = computed(() => (isOpen.value ? filter.value : ''));

const showClearButton = computed(() => {
  if (!props.clearable) return false;
  return Boolean(filter.value || model.value);
});

watch(
  [searchTerm],
  () => {
    void search(searchTerm.value);
  },
  { immediate: true },
);

watch([validationError], () => {
  log.debug('Validation error update', validationError.value);
  emit('update:error', validationError.value);
});

const getItemByIndex = (index: number): Item<T> | undefined => {
  const items = [
    ...(props.staticGroups?.flatMap((group) => group.items) ?? []),
    ...props.groups.flatMap((group) => group.items),
  ];
  return items[index];
};

const onSelectByIndex = async (index: number) => {
  const item = getItemByIndex(index);
  log.debug(`Select by index: ${index}`, item);
  if (!item) return;

  await onSelect(item);
};

const closeDropdown = () => {
  log.debug('Close dropdown');
  combobox.value.closeComboboxList();
};

const blur = () => {
  log.debug('Blur search input');
  searchInput.value.blur();
};

const onSelect = async (item: Item<T>) => {
  if (item.disabled) {
    log.debug('Selected disabled item. Return.', item);
    return;
  }
  log.debug('Select item, closing dropdown', item);
  // we set open state here to prevent sending search event
  isOpen.value = false;
  closeDropdown();
  filter.value = item.title;
  model.value = item;
  await nextTick();
  validateValue();
  blur();
};

const validateValue = () => {
  log.debug('Validate value', model.value);
  if (props.required && !model.value) {
    log.debug('Setting required error');
    validationError.value = SearchDropdownError.REQUIRED;
    return;
  }
  if (
    props.minValue != null &&
    model.value?.comparableValue != null &&
    model.value.comparableValue < props.minValue
  ) {
    log.debug('Setting minValue error');
    validationError.value = SearchDropdownError.MIN_VALUE;
    return;
  }
  if (
    props.maxValue != null &&
    model.value?.comparableValue != null &&
    model.value.comparableValue > props.maxValue
  ) {
    log.debug('Setting maxValue error');
    validationError.value = SearchDropdownError.MAX_VALUE;
    return;
  }
  validationError.value = null;
};

watch(
  [model],
  () => {
    log.debug('Model update', model.value);
    if (!isOpen.value) {
      filter.value = model.value?.title ?? '';
    }
  },
  {
    immediate: true,
  },
);

const onOpened = (open: boolean) => {
  log.debug('Open state update', open);
  isOpen.value = open;
  if (!open) {
    filter.value = model.value?.title ?? '';
  }
  emit('update:opened', open);
};

const onClear = async () => {
  if (!props.clearable) return;
  log.debug('Clear search dropdown');
  filter.value = '';
  model.value = null;
  emit('update:search', '');
  await nextTick();
  validateValue();
};

const allGroupsEmpty = computed(() => {
  return groupsWithItems.value.length === 0;
});

const validationTrigger = inject<Ref<number>>(TRIGGER_FORM_VALIDATION, ref(0));

watch([validationTrigger], () => {
  log.debug('Validation trigger');
  validateValue();
});

watch(
  [() => props.required, () => props.minValue, () => props.maxValue],
  () => {
    log.debug(`Value requirements changed`);
    validateValue();
  },
);

type InputMessage = {
  message: string;
  type: 'error' | 'success' | 'warning';
};
const validationMessages = computed(() => {
  const result: InputMessage[] = [];
  if (validationError.value) {
    const message = props.errorMessages?.[validationError.value];
    if (!message) {
      log.error('No i18n for error', validationError.value);
      return;
    }
    result.push({
      message,
      type: 'error',
    });
  }

  return result;
});

const noResultsMessage = computed(() => {
  // TODO[SEAN]: update after error type enum is added
  const messageKey = props.error
    ? (props.error.response.body as unknown as { type: string | null }).type ??
      SearchDropdownMessage.ERROR_LOADING_RESULTS
    : SearchDropdownMessage.NO_RESULTS;
  const message = props.messages[messageKey];
  if (!message) {
    log.error('No i18n for message', messageKey);
    return;
  }
  return message;
});
</script>
<template>
  <div :data-qa="DATA_QA.ROOT">
    <DtRecipeComboboxWithPopover
      ref="combobox"
      :label="label"
      :description="description"
      :list-aria-label="listAriaLabel"
      :loading="loadingOrDebounce"
      :list-class="listClass"
      :size="size"
      content-width="anchor"
      max-height="300px"
      @select="onSelectByIndex"
      @escape="blur"
      @opened="onOpened"
    >
      <template #input="{ inputProps }">
        <DtInput
          v-bind="inputProps"
          ref="searchInput"
          v-model="filter"
          class="d-to-ellipsis"
          :data-qa="DATA_QA.INPUT"
          :data-open="isOpen"
          :data-loading="loadingOrDebounce"
          :data-qa-loading="loadingOrDebounce"
          :disabled="disabled"
          :placeholder="model ? model.title : placeholder"
          :messages="validationMessages"
          @focus="filter = ''"
          @keydown.tab="closeDropdown"
        >
          <template v-if="model?.icon" #leftIcon>
            <DtIcon
              :data-qa="DATA_QA.INPUT_ICON"
              :name="model?.icon"
              size="300"
              :class="model.iconClasses"
            />
          </template>
          <template v-if="rightIcon || clearable" #rightIcon>
            <DtButton
              v-if="showClearButton"
              class="d-m4 d-p2 d-mrn4"
              :data-qa="DATA_QA.RIGHT_ICON"
              circle
              importance="clear"
              @click="onClear"
            >
              <template #icon>
                <DtIcon name="close" size="200" />
              </template>
            </DtButton>
            <DtIcon
              v-else
              :data-qa="DATA_QA.RIGHT_ICON"
              :name="rightIcon"
              class="d-mrn1"
              size="200"
            />
          </template>
        </DtInput>
      </template>
      <template #list="{ listProps }">
        <ul class="d-p4" :data-qa="DATA_QA.DROPDOWN" v-bind="listProps">
          <div v-if="staticGroups">
            <div v-for="(group, groupIndex) in staticGroups" :key="groupIndex">
              <div
                v-if="groupIndex !== 0 && !group.noDivider"
                :data-qa="DATA_QA.DIVIDER"
                class="d-bb d-bc-default d-mxn4 d-my4"
              />
              <DtListItemGroup
                :heading="group.title"
                class="d-ps-static"
                :data-qa="DATA_QA.STATIC_GROUP"
                heading-class="d-fw-bold d-fc-muted d-fs-100 d-ml8"
              >
                <DtListItem
                  v-for="item of group.items"
                  :key="item.key"
                  :class="{
                    'd-c-not-allowed': item.disabled,
                    'd-fc-disabled': item.disabled,
                  }"
                  :aria-disabled="item.disabled"
                  :aria-label="
                    item.disabled
                      ? $t('SEARCH_DROPDOWN_DISABLED_ITEM_ARIA_LABEL', {
                          title: item.title,
                          subtitle: item.subtitle,
                        })
                      : ''
                  "
                  :data-qa="DATA_QA.ITEM"
                  :data-meta="item.key"
                  navigation-type="arrow-keys"
                  role="option"
                  @click="onSelect(item)"
                >
                  <template v-if="showListIcons && item.icon" #left>
                    <DtIcon
                      :data-qa="DATA_QA.ITEM_ICON"
                      :name="item.icon"
                      :class="item.iconClasses"
                      size="300"
                    />
                  </template>
                  <template #subtitle>
                    <span :class="{ 'd-fc-disabled': item.disabled }">
                      {{ item.subtitle }}
                    </span>
                  </template>
                  <div :class="item.titleClasses">
                    {{ item.title }}
                  </div>
                </DtListItem>
              </DtListItemGroup>
            </div>
            <div
              v-if="!allGroupsEmpty"
              :data-qa="DATA_QA.DIVIDER"
              class="d-bb d-bc-default d-mxn4 d-my4"
            />
          </div>
          <div
            v-if="allGroupsEmpty && staticGroups"
            :data-qa="DATA_QA.DIVIDER"
            class="d-bb d-bc-default d-mxn4 d-my4"
          />
          <div
            v-if="allGroupsEmpty"
            class="d-d-flex d-ai-center d-fc-muted d-ml8"
          >
            <DtIcon
              v-if="error"
              :data-qa="DATA_QA.NO_RESULTS_ICON"
              class="d-mr8"
              name="alert-circle"
              size="300"
            />
            <span>{{ noResultsMessage }}</span>
          </div>
          <div
            v-for="(group, groupIndex) in groupsWithItems"
            v-else
            :key="groupIndex"
          >
            <div
              v-if="groupIndex !== 0 && !group.noDivider"
              :data-qa="DATA_QA.DIVIDER"
              class="d-bb d-bc-default d-mxn4 d-my4"
            />
            <DtListItemGroup
              v-if="group.items.length > 0"
              class="d-ps-static"
              :data-qa="DATA_QA.GROUP"
              :heading="group.title"
              heading-class="d-fw-bold d-fc-muted d-fs-100 d-ml8"
            >
              <DtListItem
                v-for="item of group.items"
                :key="item.key"
                :class="{
                  'd-c-not-allowed': item.disabled,
                  'd-fc-disabled': item.disabled,
                }"
                :aria-disabled="item.disabled"
                :aria-label="
                  item.disabled
                    ? $t('SEARCH_DROPDOWN_DISABLED_ITEM_ARIA_LABEL', {
                        title: item.title,
                        subtitle: item.subtitle,
                      })
                    : ''
                "
                :data-qa="DATA_QA.ITEM"
                :data-meta="item.key"
                navigation-type="arrow-keys"
                role="option"
                @click="onSelect(item)"
              >
                <template v-if="showListIcons && item.icon" #left>
                  <DtIcon
                    :data-qa="DATA_QA.ITEM_ICON"
                    :name="item.icon"
                    :class="item.iconClasses"
                    size="300"
                  />
                </template>
                <template #subtitle>
                  <span :class="{ 'd-fc-disabled': item.disabled }">
                    {{ item.subtitle }}
                  </span>
                </template>
                <div
                  :class="['d-to-ellipsis', 'd-of-hidden', item.titleClasses]"
                >
                  {{ item.title }}
                </div>
              </DtListItem>
            </DtListItemGroup>
          </div>
        </ul>
      </template>
    </DtRecipeComboboxWithPopover>
  </div>
</template>
